import React from 'react'
import { AppNotification } from '@/services/notificationService'

interface NotificationPanelProps {
  notifications: AppNotification[]
  onMarkRead: (notificationId: string) => void
  onMarkAllRead: () => void
}

const MAX_DISPLAYED = 20

const relativeTime = (iso: string): string => {
  const diffMs = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(diffMs / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  notifications,
  onMarkRead,
  onMarkAllRead
}) => {
  const displayed = notifications.slice(0, MAX_DISPLAYED)
  const hasUnread = notifications.some((n) => !n.isRead)

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
        {hasUnread && (
          <button
            type="button"
            onClick={onMarkAllRead}
            className="text-xs text-primary-600 hover:underline"
          >
            Mark all as read
          </button>
        )}
      </div>

      <div className="max-h-96 overflow-y-auto">
        {displayed.length === 0 && (
          <p className="px-4 py-6 text-sm text-gray-600 text-center">No notifications yet.</p>
        )}

        {displayed.map((notification) => (
          <div
            key={notification.id}
            className={`px-4 py-3 border-b last:border-b-0 ${notification.isRead ? '' : 'bg-primary-50'}`}
          >
            <div className="flex items-start gap-2">
              {!notification.isRead && (
                <span className="mt-1.5 w-2 h-2 bg-primary-600 rounded-full flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`text-sm ${notification.isRead ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>
                  {notification.title}
                </p>
                <p className="text-sm text-gray-600 line-clamp-2">{notification.body}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-400">{relativeTime(notification.createdAt)}</span>
                  {!notification.isRead && (
                    <button
                      type="button"
                      onClick={() => onMarkRead(notification.id)}
                      className="text-xs text-primary-600 hover:underline"
                    >
                      Mark as read
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default NotificationPanel
